import { DonorWall, WallOptIn } from '../components/DonorWall';
import React, { useState, useEffect } from 'react';
import { Heart, Loader2, BookOpen, Home, Globe, Laptop, Flame, HandHeart } from 'lucide-react';
import ContentLayout from '../components/ContentLayout';
import api from '../lib/api';
import { useToast } from '../hooks/use-toast';

const ICONS = { HandHeart, BookOpen, Home, Globe, Laptop, Flame, Heart };

const FALLBACK_FUNDS = [
  { name: 'General Donation', icon: 'HandHeart', description: 'Support the overall functioning of our mission — daily operations, outreach, and the sharing of meditation and awareness.' },
];

const PRESETS = [21, 51, 108, 251, 501, 1008];

const DonatePage = () => {
  const { toast } = useToast();
  const [funds, setFunds] = useState([]);
  const [fund, setFund] = useState('');
  const [amount, setAmount] = useState(108);
  const [custom, setCustom] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [wall, setWall] = useState({ opt_in: false, style: 'first_initial' });
  const [monthly, setMonthly] = useState(false);

  useEffect(() => {
    api.get('/donations/funds')
      .then((r) => { const f = (r.data && r.data.length) ? r.data : FALLBACK_FUNDS; setFunds(f); setFund(f[0].name); })
      .catch(() => { setFunds(FALLBACK_FUNDS); setFund(FALLBACK_FUNDS[0].name); });
  }, []);

  const finalAmount = custom ? parseFloat(custom) : amount;

  const donate = async () => {
    if (!finalAmount || finalAmount <= 0) return toast({ title: 'Please enter a donation amount' });
    if (!email) return toast({ title: 'Please enter your email for the receipt' });
    if (monthly && finalAmount < 5) return toast({ title: 'Monthly gifts start at $5' });
    setLoading(true);
    try {
      const res = await api.post(monthly ? '/donations/monthly/checkout' : '/donations/checkout', {
        amount: finalAmount, fund, name, email, origin_url: window.location.origin, wall_opt_in: wall.opt_in, wall_name_style: wall.style,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast({ title: 'Could not start checkout', description: e?.response?.data?.detail || 'Please try again.' });
      setLoading(false);
    }
  };

  return (
    <ContentLayout
      testid="page-donate"
      eyebrow="Support Our Work"
      titleTop="Give with an"
      titleItalic="Open Heart"
      intro="We are nurturing several projects dedicated to spreading Osho’s vision and meditation practices. If you feel inspired, your contribution helps keep this work alive and accessible to all."
    >
      <div className="grid lg:grid-cols-[1fr_400px] gap-10 items-start">
        {/* Funds */}
        <div className="grid sm:grid-cols-2 gap-4">
          {funds.map(({ name: key, icon, description: desc }) => {
            const Icon = ICONS[icon] || HandHeart;
            return (
            <button key={key} onClick={() => setFund(key)} data-testid={`fund-${key.replace(/[^a-z]/gi, '-').toLowerCase()}`}
              className={`text-left p-5 rounded-2xl border transition-all ${fund === key ? 'bg-mahogany text-cream border-mahogany shadow-lg' : 'bg-white/70 border-rose/40 hover:shadow-md'}`}>
              <Icon className={`w-6 h-6 mb-3 ${fund === key ? 'text-cream' : 'text-rose-italic'}`} />
              <div className={`font-serif-display text-lg mb-1 ${fund === key ? 'text-cream' : 'text-burgundy'}`}>{key}</div>
              <p className={`text-sm ${fund === key ? 'text-cream/85' : 'text-burgundy/75'}`}>{desc}</p>
            </button>
          ); })}
        </div>

        {/* Donation form */}
        <div className="p-6 rounded-3xl bg-white/80 border border-rose/40 shadow-sm sticky top-24" data-testid="donate-form">
          <div className="flex items-center gap-2 text-mahogany mb-4"><Heart className="w-5 h-5 text-rose-italic" /> <span className="font-serif-display text-2xl text-burgundy">Your Donation</span></div>
          <div className="text-xs uppercase tracking-wider text-mahogany/60 mb-2">Donating to</div>
          <div className="text-burgundy font-medium mb-4">{fund}</div>
          <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-rose-soft/40 mb-4" data-testid="donate-frequency">
            {[[false, 'One time'], [true, 'Monthly']].map(([v, l]) => (
              <button key={l} type="button" onClick={() => setMonthly(v)} data-testid={`donate-frequency-${v ? 'monthly' : 'once'}`}
                className={`py-1.5 rounded-full text-sm transition-colors ${monthly === v ? 'bg-mahogany text-cream' : 'text-mahogany/80 hover:text-burgundy'}`}>{l}</button>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-2 mb-3">
            {PRESETS.map((a) => (
              <button key={a} onClick={() => { setAmount(a); setCustom(''); }} data-testid={`amount-${a}`}
                className={`py-2 rounded-xl border text-sm transition-colors ${!custom && amount === a ? 'bg-mahogany text-cream border-mahogany' : 'bg-white text-mahogany/80 border-rose/50 hover:bg-rose-soft/40'}`}>${a}</button>
            ))}
          </div>
          <div className="relative mb-4">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-mahogany/60">$</span>
            <input type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Other amount" data-testid="custom-amount"
              className="w-full bg-white rounded-xl border border-rose/50 pl-7 pr-3 py-2 text-burgundy focus:outline-none" />
          </div>

          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name (optional)" data-testid="donor-name"
            className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none mb-3" />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for receipt *" data-testid="donor-email"
            className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none mb-2" />
          <div className="mb-4"><WallOptIn wall={wall} setWall={setWall} /></div>

          <button onClick={donate} disabled={loading} className="pill-btn w-full justify-center" data-testid="donate-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{monthly ? `Give $${finalAmount || 0} monthly` : `Donate $${finalAmount || 0}`} <Heart className="w-4 h-4" /></>}
          </button>
          <p className="text-xs text-mahogany/60 mt-3 text-center">Secure payment via Stripe — Apple Pay, Google Pay or card. You’ll be redirected to complete your gift.</p>
          {monthly && <p className="text-xs text-mahogany/60 mt-2 text-center" data-testid="monthly-note">A receipt is emailed after every monthly gift. Change or cancel any time — <a href="/giving/manage" className="underline text-rose-italic">manage my monthly gift</a>.</p>}
        </div>
      </div>

      <DonorWall />

      <p className="mt-12 text-center text-mahogany/75 text-sm max-w-2xl mx-auto">
        We also welcome offerings such as Osho books, rare materials, and personal artifacts connected to Osho.
        For non-monetary contributions, please reach out via the <a href="/contacts" className="text-rose-italic hover:underline">Contact Us</a> page.
      </p>
    </ContentLayout>
  );
};

export default DonatePage;
